// DAEMON — splits: the bridge between a live run and the record book.
//
// `records.ts` is the ledger — pure, tested, and completely ignorant of
// how a run actually happens. `DaemonGame.tsx` is the shell — React,
// canvas, pointer lock. This module is the seam between them, and it
// exists so that the seam is testable without mounting a component.
//
// Three jobs:
//
//   1. **Watch a run and turn it into marks.** The sim already announces
//      `card-clear` and `phase-clear` with the tick they happened on, so
//      there is nothing to measure — only something to record.
//   2. **Commit a finished run** to storage, whatever "finished" meant.
//      A quit counts; see `commitRun`.
//   3. **Build the rows** the live panel and the results screen draw.
//
// The hard rule, inherited from telemetry: **records read state, they
// never write it.** Nothing in here is reachable from `step()`, nothing
// in here allocates per tick, and every storage call is wrapped so that
// a full quota or a sandboxed iframe costs a logbook entry and never a
// run.

import { utcDayKey } from "./daily";
import { phaseAt } from "./phases";
import {
  applyRun,
  compareSplits,
  emptyRecords,
  loadRecords,
  resultFromState,
  saveRecords,
  splitsFrom,
  type Improvement,
  type Records,
  type RunResult,
  type Split,
} from "./records";
import type { PhaseId, SimEvent, SimState } from "./types";

// ── Storage keys ──────────────────────────────────────────────────────
//
// Both go through `host.storageKey`, which is the whole reason it
// exists: a PB set standalone at daemon.skenmy.com and one set inside
// skenOS are different logbooks, and merging them would make both a
// lie about which build the time was played on.

export const RECORDS_SUFFIX = "records.v1";
export const SPLITS_PREF_SUFFIX = "splits.v1";

// ── Split keys ────────────────────────────────────────────────────────
//
// A gold is keyed on this string forever, so it has to mean the same
// thing in every run that ever contains it. The card **id** does: it is
// the pattern id, chosen in `phases.ts` precisely so that a reorder
// cannot silently rebind it. The card *name* does not — it is display
// text and renaming a pattern would quietly orphan its gold.
//
// Prefixed, because `records.ts` keys `cards`, `golds` and the split
// list off one flat namespace. Without the prefix a phase called `rl3`
// and a card called `rl3` would be the same row.

export const cardSplitKey = (cardId: string): string => `card:${cardId}`;
export const phaseSplitKey = (phase: PhaseId): string => `phase:${phase}`;

/** Every card key this run's plan will produce, in order. The domain
 *  `sumOfBest` has to be asked about — asking it about *all* golds ever
 *  would price a run that cannot be played. */
export function planSplitKeys(s: SimState): string[] {
  const out: string[] = [];
  for (const set of s.cardPlan) for (const c of set) out.push(cardSplitKey(c.id));
  return out;
}

// ── The tracker ───────────────────────────────────────────────────────

/** A split, plus the phase it belongs to. `records.ts` has no notion of
 *  grouping and does not need one; the panel does, so it lives here. */
export type SplitMark = {
  key: string;
  name: string;
  at: number;
  phase: PhaseId;
};

export type SplitTracker = {
  /** Called with each tick's events, exactly like `Telemetry.observe`. */
  observe: (events: readonly SimEvent[], state: SimState) => void;
  marks: () => readonly SplitMark[];
  /** The marks as `records.ts` wants them — segment durations derived. */
  splits: () => Split[];
  /** Upgrades *offered* across the run, including drafts that were
   *  skipped and the one the player quit on. Offered-vs-taken is the
   *  only way to tell a weak upgrade from an unreadable one. */
  offered: () => readonly string[];
  /** The card on screen right now, as a split key. This is what a death
   *  gets attributed to. */
  lastCard: () => string | null;
};

/**
 * Watch a run.
 *
 * Created per run, from the state `createRun` returned — which is where
 * the card plan is read from, because the sim announces cards by
 * **name** and golds are keyed by **id**. Both are drawn from the same
 * `SpellCard`, so within a run the map is exact; building it once at the
 * start costs one pass over ten cards and nothing thereafter.
 */
export function createSplitTracker(initial: SimState): SplitTracker {
  const idByName = new Map<string, string>();
  for (const set of initial.cardPlan) {
    for (const c of set) if (!idByName.has(c.name)) idByName.set(c.name, c.id);
  }

  const marks: SplitMark[] = [];
  const offered: string[] = [];
  let inDraft = false;
  let phase: PhaseId = phaseAt(initial.phaseIndex).id;
  const firstCard = initial.cardPlan[initial.phaseIndex]?.[initial.cardIndex];
  let current: string | null = firstCard ? cardSplitKey(firstCard.id) : null;

  // An unknown name can only mean the plan and the event stream
  // disagree, which is a bug — but falling back to the name keeps the
  // run recordable instead of throwing inside a frame callback.
  const keyOf = (name: string): string => cardSplitKey(idByName.get(name) ?? name);

  return {
    observe(events, state) {
      for (const e of events) {
        switch (e.t) {
          case "phase-start":
            phase = e.phase;
            break;
          case "card-start":
            current = keyOf(e.name);
            break;
          case "card-clear":
            marks.push({ key: keyOf(e.name), name: e.name, at: e.ticks, phase });
            break;
          case "phase-clear": {
            // The sim clears the last card of a phase and the phase
            // itself on the *same tick*, so the phase segment is
            // normally zero-length: a row that can never be beaten, a
            // gold that is always 0, and a junk entry in the per-card
            // stats `records.ts` keys off the same namespace. Recorded
            // only when it actually spans time — which keeps the
            // handling honest if the sim ever puts something between
            // the two.
            const last = marks[marks.length - 1];
            if (!last || e.ticks > last.at) {
              marks.push({ key: phaseSplitKey(e.phase), name: e.phase, at: e.ticks, phase: e.phase });
            }
            break;
          }
          default:
            break;
        }
      }

      // The draft, on the edge into it rather than on the way out.
      // Someone who closes the tab staring at three cards still saw
      // three cards, and a pick rate that silently drops those offers
      // flatters every upgrade in the game.
      if (state.status === "draft") {
        if (!inDraft) {
          inDraft = true;
          for (const id of state.offers) offered.push(id);
        }
      } else {
        inDraft = false;
      }
    },
    marks: () => marks,
    splits: () => splitsFrom(marks.map((m) => ({ key: m.key, name: m.name, at: m.at }))),
    offered: () => offered,
    lastCard: () => current,
  };
}

// ── Rows ──────────────────────────────────────────────────────────────

/** One line of the split panel. Everything the view needs, nothing it
 *  has to compute. */
export type SplitRow = {
  key: string;
  name: string;
  phase: PhaseId;
  /** Cumulative tick this segment ended on. */
  at: number;
  /** The segment itself — what a gold is measured on. */
  duration: number;
  /** Cumulative delta against the reference run. Positive is time lost.
   *  Null when the reference has never seen this segment. */
  delta: number | null;
  /** Beat the best-ever time for this segment. */
  gold: boolean;
};

/**
 * Rows for the panel.
 *
 * `golds` must be the snapshot taken **before** the run started. Reading
 * live records here would compare a segment against itself the instant
 * it was written and no row would ever be gold.
 */
export function buildRows(
  marks: readonly SplitMark[],
  reference: readonly Split[] | undefined,
  golds: Readonly<Record<string, number>>,
): SplitRow[] {
  const splits = splitsFrom(marks.map((m) => ({ key: m.key, name: m.name, at: m.at })));
  const compared = compareSplits(splits, reference ? [...reference] : undefined);
  return splits.map((sp, i) => {
    const best = golds[sp.key];
    return {
      key: sp.key,
      name: sp.name,
      phase: marks[i]?.phase ?? "rl5",
      at: sp.at,
      duration: sp.duration,
      delta: compared[i]?.delta ?? null,
      // Same rule `applyRun` uses, deliberately: a first-ever segment is
      // a gold. Two different answers to "was that a gold?" on one
      // screen would be worse than a slightly noisy first run.
      gold: best === undefined || sp.duration < best,
    };
  });
}

/** Cumulative time the reference run had reached by the end of each
 *  segment, for the live "you are N behind" readout. */
export function referenceAt(reference: readonly Split[] | undefined): Map<string, number> {
  const m = new Map<string, number>();
  for (const s of reference ?? []) m.set(s.key, s.at);
  return m;
}

// ── Committing a run ──────────────────────────────────────────────────

export type RunRecord = {
  run: RunResult;
  improvement: Improvement;
  /** The book as it stood *before* this run — the only correct thing to
   *  compare a results screen against. */
  previous: Records;
  /** The book after. Already saved, if saving was possible. */
  records: Records;
};

/**
 * Fold a finished run into storage.
 *
 * Called on a win, on a loss, and on a quit — where "quit" covers
 * closing the tab, closing the skenOS window, and pressing Shift-R
 * halfway through something that was going badly. All three are runs;
 * only counting the ones that reached an ending would make the win rate
 * a compliment rather than a number.
 *
 * `storage` is injected rather than reached for, so a test can hand it a
 * throwing stub and a sandboxed iframe can hand it null. Every path
 * returns null instead of throwing: a records failure must be invisible
 * to the player mid-run.
 */
export function commitRun(opts: {
  state: SimState;
  outcome: "won" | "lost" | "quit";
  tracker: SplitTracker;
  storage: Storage | null;
  key: string;
  now?: number;
  /** UTC day for the daily streak. Defaults to today; passed explicitly
   *  by tests, and never computed inline anywhere else — `daily.ts` owns
   *  what "today" means and a second definition would drift. */
  day?: string;
}): RunRecord | null {
  try {
    const previous = opts.storage ? loadRecords(opts.storage, opts.key) : emptyRecords();
    const won = opts.outcome === "won";
    const run = resultFromState(opts.state, {
      won,
      splits: opts.tracker.splits(),
      offered: [...opts.tracker.offered()],
      // A won run has no card to blame. `records.ts` only reads this on
      // a loss, but handing it a card anyway would be an invitation.
      lastCard: won ? null : opts.tracker.lastCard(),
    });
    const { records, improvement } = applyRun(previous, run, {
      outcome: opts.outcome,
      day: opts.state.config.daily ? (opts.day ?? utcDayKey()) : undefined,
      now: opts.now ?? Date.now(),
    });
    if (opts.storage) saveRecords(records, opts.storage, opts.key);
    return { run, improvement, previous, records };
  } catch {
    // Quota, a hostile Storage, a hand-edited blob that survived
    // validation. The run still happened; the logbook simply missed it.
    return null;
  }
}

// ── Storage plumbing ──────────────────────────────────────────────────

/** localStorage, or null. Sandboxed iframes throw on *access*, not just
 *  on use, so the property read itself has to be guarded. */
export function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** Read a Records book without ever throwing. */
export function readRecords(storage: Storage | null, key: string): Records {
  if (!storage) return emptyRecords();
  try {
    return loadRecords(storage, key);
  } catch {
    return emptyRecords();
  }
}

/** Is the split panel on? Off is the answer for anyone who has not
 *  asked: a first-time visitor shown a split timer has been handed
 *  somebody else's hobby. */
export function loadSplitPref(storage: Storage | null, key: string): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(key) === "on";
  } catch {
    return false;
  }
}

export function saveSplitPref(on: boolean, storage: Storage | null, key: string): void {
  if (!storage) return;
  try {
    storage.setItem(key, on ? "on" : "off");
  } catch {
    /* quota — the panel still works, it just forgets */
  }
}
